import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import UsageStats from '../../components/dashboard/UsageStats';
import ProFeatures from '../../components/subscription/ProFeatures';
import SubscriptionManagement from '../../components/SubscriptionManagement/SubscriptionManagement';
import subscriptionService from '../../services/subscription.service';

// Log when this module is imported
console.log('📋 SubscriptionPage module loaded');

const SubscriptionPage = () => {
  console.log('📍 SubscriptionPage function executing');
  
  // Initialize state variables
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    console.log('🚀 SubscriptionPage component mounted');
    
    const fetchSubscription = async () => {
      // Set default loading state
      setLoading(true);
      setError(null);
      
      console.log('💬 Attempting to call subscriptionService.getStatus()');
      console.log('💬 Current user:', currentUser?.uid || 'No user');
      console.log('💬 subscription service:', subscriptionService ? 'Loaded' : 'Not loaded');
      console.log('💬 getStatus method:', typeof subscriptionService?.getStatus === 'function' ? 'Exists' : 'Missing');
      
      try {
        // Use the subscription service
        console.log('📡 Calling API: subscriptionService.getStatus()');
        const response = await subscriptionService.getStatus();
        console.log('✅ API response received:', response?.status || 'No status');
        
        // Handle standardized API response format
        if (response.data && response.data.status === 'success') {
          console.log('✅ Setting subscription data:', response.data.data);
          setSubscription(response.data.data);
        } else {
          console.error('❌ Error in API response:', response?.data?.message || 'Unknown error');
          setError('Unable to load subscription data. Please try again later.');
          // Set default free plan if there's an error
          setSubscription({ status: 'free' });
        }
      } catch (error) {
        console.error('❌ Error fetching subscription:', error);
        setError('Network error when loading subscription. Please check your connection.');
        // Set default free plan if there's an error
        setSubscription({ status: 'free' });
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      console.log('👤 User authenticated, fetching subscription');
      fetchSubscription();
    } else {
      console.log('👤 No authenticated user, defaulting to free plan');
      // If no user, set as free plan and stop loading
      setSubscription({ status: 'free' });
      setLoading(false);
    }
  }, [currentUser]);
  
  const isPro = subscription?.status === 'active';
  
  try {
    console.log('🖌️ Rendering subscription UI, subscription status:', subscription?.status || 'unknown');
    
    if (loading) {
      return (
        <Container fluid className="py-4">
          <h1 className="h3 mb-4 d-flex align-items-center">
            <i className="bi bi-wallet2 me-2 text-primary"></i>
            Subscription Management
          </h1>
          
          <div className="d-flex justify-content-center align-items-center py-5" style={{minHeight: '300px'}}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading your subscription details...</p>
            </div>
          </div>
        </Container>
      );
    }
    
    // Show error message if there's an error
    // But still render the page with default free plan
    
    return (
      <Container fluid className="py-4">
          <h1 className="h3 mb-4 d-flex align-items-center">
            <i className="bi bi-wallet2 me-2 text-primary"></i>
            Subscription Management
          </h1>
          
          {error && (
            <Alert variant="warning" className="mb-4 shadow-sm border-0" dismissible>
              <Alert.Heading><i className="bi bi-exclamation-triangle me-2"></i>Something went wrong</Alert.Heading>
              <p>We encountered an issue fetching your subscription details. Please try again later.</p>
              {process.env.NODE_ENV === 'development' && (
                <pre className="mt-3 p-2 bg-light">{JSON.stringify(error, null, 2)}</pre>
              )}
            </Alert>
          )}
          
          <Row>
            <Col lg={5}>
              <SubscriptionManagement externalSubscription={subscription} />
              <UsageStats />
            </Col>
            
            <Col lg={7}>
              <Card className="mb-4">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Plan Comparison</h5>
                  <span className="badge bg-primary">$9.00/month</span>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Feature</th>
                          <th className="text-center">Free Plan</th>
                          <th className="text-center">Pro Plan</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Monthly Clicks</td>
                          <td className="text-center">1,000</td>
                          <td className="text-center text-success"><strong>Unlimited</strong></td>
                        </tr>
                        <tr>
                          <td>Monthly Conversions</td>
                          <td className="text-center">50</td>
                          <td className="text-center text-success"><strong>Unlimited</strong></td>
                        </tr>
                        <tr>
                          <td>Priority Email Support</td>
                          <td className="text-center">❌</td>
                          <td className="text-center">✅</td>
                        </tr>
                        <tr>
                          <td>Enhanced Export Options</td>
                          <td className="text-center">❌</td>
                          <td className="text-center">✅</td>
                        </tr>
                        <tr>
                          <td>Detailed Analytics</td>
                          <td className="text-center">Basic</td>
                          <td className="text-center">Advanced</td>
                        </tr>
                        <tr>
                          <td>Email Reports</td>
                          <td className="text-center">❌</td>
                          <td className="text-center">✅</td>
                        </tr>
                        <tr>
                          <td>Priority Support</td>
                          <td className="text-center">❌</td>
                          <td className="text-center">✅</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
              
              <ProFeatures isPro={isPro} />
            </Col>
          </Row>
        </Container>
      );
  } catch (renderError) {
    console.error('❌ Error rendering subscription page:', renderError);
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <h4>Error Loading Subscription Page</h4>
          <p>There was a problem loading the subscription information. Please try again later.</p>
          <p><small>Error: {renderError.message}</small></p>
        </Alert>
      </Container>
    );
  }
};

export default SubscriptionPage;
